import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../services/api";
import useAuthenticate from "../../../hooks/useAuthenticate";
import Navbar from "src/components/organisms/navbar";
import {
  PageLocationTitle,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
} from "./styles";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

interface Tournament {
  id: number;
  name: string;
  slug: string;
  taxaPlataforma: number | null;
  taxaPagbank: number | null;
  valorCadastroEvento: number | null;
  valorAcompanhamento: number | null;
}

interface LocalPaymentInfo {
  resultManual: { value: string; occurrences: number }[];
  resultAuto: { value: string; occurrences: number }[];
  totalPaid: number;
  totalUnpaid: number;
}

interface BalanceInfo {
  grossManual: number;
  grossAuto: number;
  countManual: number;
  countAuto: number;
  totalPaid: number;
  totalUnpaid: number;
  totalRevenue: number;
  taxaPlataforma: number;
  feeAmount: number;
  netRevenue: number;
  alreadyPaid: number;
  pendingAmount: number;
  serviceCharges: number;
  availableBalance: number;
}

interface Withdrawal {
  id: number;
  amount: string;
  pixKey: string | null;
  status: string;
  type: string;
  description: string | null;
  rejectionReason: string | null;
  requestedAt: string;
  reviewedAt: string | null;
  paidAt: string | null;
  tournament: { id: number; name: string; slug: string };
  requester?: { id: number; name: string; email: string };
}

const statusConfig: Record<string, { label: string; color: "warning" | "info" | "error" | "success" }> = {
  PENDING: { label: "Pendente", color: "warning" },
  APPROVED: { label: "Aprovado", color: "info" },
  REJECTED: { label: "Rejeitado", color: "error" },
  PAID: { label: "Pago", color: "success" },
};

const formatDate = (date: string | null) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PaymentManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken, account } = useAuthenticate();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isAdmin = isAdminRoute && account?.roles?.includes("manager") === true;

  // State financeiro
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournaments, setSelectedTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(false);
  const [localInfo, setLocalInfo] = useState<LocalPaymentInfo | null>(null);

  // State saque (dono)
  const [balanceMap, setBalanceMap] = useState<Record<number, BalanceInfo>>({});
  const [ownerWithdrawals, setOwnerWithdrawals] = useState<Withdrawal[]>([]);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawPixKey, setWithdrawPixKey] = useState("");
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [selectedTournamentForWithdraw, setSelectedTournamentForWithdraw] = useState<Tournament | null>(null);

  // State admin saques
  const [adminTab, setAdminTab] = useState(0);
  const [allWithdrawals, setAllWithdrawals] = useState<Withdrawal[]>([]);
  const [adminWithdrawalsLoading, setAdminWithdrawalsLoading] = useState(false);
  const [adminStatusFilter, setAdminStatusFilter] = useState<string>("");
  const [approveDialog, setApproveDialog] = useState<Withdrawal | null>(null);
  const [rejectDialog, setRejectDialog] = useState<Withdrawal | null>(null);
  const [paidDialog, setPaidDialog] = useState<Withdrawal | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // State histórico saques por torneio (admin tab 0)
  const [adminTournamentWithdrawals, setAdminTournamentWithdrawals] = useState<Withdrawal[]>([]);

  // State camisas
  type ShirtDetail = { subscriptionId: number | null; tshirtId: number; athleteName: string; size: string; measure: string; price: number; status: "paid" | "pending" | "orphan"; paymentType: "auto" | "manual" | null };
  const [shirtsData, setShirtsData] = useState<{
    pending: { size: string; quantity: number; price: number; totalPrice: number }[];
    paid: { size: string; quantity: number; price: number; totalPrice: number }[];
    details: ShirtDetail[];
  } | null>(null);
  const [shirtsLoading, setShirtsLoading] = useState(false);
  const [shirtsFilter, setShirtsFilter] = useState<"all" | "paid" | "pending" | "orphan">("all");
  const [shirtsSearch, setShirtsSearch] = useState("");
  const [shirtsSortField, setShirtsSortField] = useState<string | null>(null);
  const [shirtsSortDir, setShirtsSortDir] = useState<"asc" | "desc">("asc");

  // State tabelas genéricas (busca + ordenação)
  const [ownerWithdrawalsSearch, setOwnerWithdrawalsSearch] = useState("");
  const [ownerWithdrawalsSortField, setOwnerWithdrawalsSortField] = useState<string | null>(null);
  const [ownerWithdrawalsSortDir, setOwnerWithdrawalsSortDir] = useState<"asc" | "desc">("asc");

  const [adminTournamentWdSearch, setAdminTournamentWdSearch] = useState("");
  const [adminTournamentWdSortField, setAdminTournamentWdSortField] = useState<string | null>(null);
  const [adminTournamentWdSortDir, setAdminTournamentWdSortDir] = useState<"asc" | "desc">("asc");

  const [adminWdSearch, setAdminWdSearch] = useState("");
  const [adminWdSortField, setAdminWdSortField] = useState<string | null>(null);
  const [adminWdSortDir, setAdminWdSortDir] = useState<"asc" | "desc">("asc");

  // State service charge (admin)
  const [serviceChargeDialogOpen, setServiceChargeDialogOpen] = useState(false);
  const [serviceChargeAmount, setServiceChargeAmount] = useState("");
  const [serviceChargeDescription, setServiceChargeDescription] = useState("");
  const [serviceChargeLoading, setServiceChargeLoading] = useState(false);
  const [serviceChargeTournament, setServiceChargeTournament] = useState<Tournament | null>(null);

  const headers = { Authorization: `Bearer ${getToken() || ""}` };

  // Carregar torneios
  useEffect(() => {
    if (!account?.id) return;
    const endpoint = isAdmin ? "/managers/tournaments/info" : "/managers/tournaments/info/me";
    api.get(endpoint, { headers })
      .then((res) => {
        const sorted = [...res.data].sort((a: Tournament, b: Tournament) =>
          a.name.localeCompare(b.name, "pt-BR")
        );
        setTournaments(sorted);
      })
      .catch(() => toast.error("Erro ao carregar torneios"));
  }, [account]);

  // Carregar saques do admin
  useEffect(() => {
    if (isAdmin && adminTab === 1) {
      loadAdminWithdrawals();
    }
  }, [isAdmin, adminTab, adminStatusFilter]);

  const loadLocalPaymentInfo = async (tournamentIds: number[]) => {
    setLoading(true);
    try {
      const ids = tournamentIds.join(",");
      const res = await api.get(`/administrators/tournaments/payment/infos/${ids}`, { headers });
      setLocalInfo(res.data);
    } catch {
      setLocalInfo(null);
      toast.error("Erro ao carregar dados de pagamento");
    } finally {
      setLoading(false);
    }
  };

  const loadBalances = async (tournamentIds: number[]) => {
    try {
      const endpoint = isAdmin ? "/administrators/withdrawals/balance" : "/managers/withdrawals/balance";
      const results = await Promise.all(
        tournamentIds.map((id) =>
          api.get(`${endpoint}/${id}`, { headers }).then((res) => ({ id, data: res.data }))
        )
      );
      const map: Record<number, BalanceInfo> = {};
      results.forEach(({ id, data }) => {
        if (!data.error) map[id] = data;
      });
      setBalanceMap(map);
    } catch {
      setBalanceMap({});
    }
  };

  const loadOwnerWithdrawals = async (tournamentIds?: number[]) => {
    try {
      if (tournamentIds && tournamentIds.length === 1) {
        const res = await api.get(`/managers/withdrawals?tournamentId=${tournamentIds[0]}`, { headers });
        setOwnerWithdrawals(res.data);
      } else {
        const res = await api.get(`/managers/withdrawals`, { headers });
        if (tournamentIds && tournamentIds.length > 1) {
          setOwnerWithdrawals(res.data.filter((w: Withdrawal) => tournamentIds.includes(w.tournament.id)));
        } else {
          setOwnerWithdrawals(res.data);
        }
      }
    } catch {
      setOwnerWithdrawals([]);
    }
  };

  const loadShirtsData = async (tournamentIds: number[]) => {
    setShirtsLoading(true);
    try {
      const res = await api.get(`/administrators/tournaments/thisrts`, {
        headers,
        params: { ids: tournamentIds.join(",") },
      });
      setShirtsData(res.data);
    } catch {
      setShirtsData(null);
    } finally {
      setShirtsLoading(false);
    }
  };

  const loadAdminTournamentWithdrawals = async (tournamentIds: number[]) => {
    try {
      if (tournamentIds.length === 1) {
        const res = await api.get(`/administrators/withdrawals?tournamentId=${tournamentIds[0]}`, { headers });
        setAdminTournamentWithdrawals(res.data);
      } else {
        const res = await api.get(`/administrators/withdrawals`, { headers });
        setAdminTournamentWithdrawals(
          res.data.filter((w: Withdrawal) => tournamentIds.includes(w.tournament.id))
        );
      }
    } catch {
      setAdminTournamentWithdrawals([]);
    }
  };

  const loadAdminWithdrawals = async () => {
    setAdminWithdrawalsLoading(true);
    try {
      const params = new URLSearchParams();
      if (adminStatusFilter) params.append("status", adminStatusFilter);
      const res = await api.get(`/administrators/withdrawals?${params}`, { headers });
      setAllWithdrawals(res.data);
    } catch {
      setAllWithdrawals([]);
      toast.error("Erro ao carregar solicitações de saque");
    } finally {
      setAdminWithdrawalsLoading(false);
    }
  };

  const refreshData = async () => {
    if (selectedTournaments.length === 0) return;
    setLoading(true);
    try {
      const ids = selectedTournaments.map((t) => t.id);
      if (isAdmin) {
        await Promise.all([
          loadLocalPaymentInfo(ids),
          loadBalances(ids),
          loadAdminTournamentWithdrawals(ids),
          loadShirtsData(ids),
        ]);
      } else {
        await Promise.all([
          loadBalances(ids),
          loadOwnerWithdrawals(ids),
          loadShirtsData(ids),
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTournaments = async (selected: Tournament[]) => {
    setSelectedTournaments(selected);
    setLocalInfo(null);
    setBalanceMap({});
    setAdminTournamentWithdrawals([]);
    setShirtsData(null);

    if (selected.length === 0) return;
    const ids = selected.map((t) => t.id);

    if (isAdmin) {
      await Promise.all([
        loadLocalPaymentInfo(ids),
        loadBalances(ids),
        loadAdminTournamentWithdrawals(ids),
        loadShirtsData(ids),
      ]);
    } else {
      await Promise.all([
        loadBalances(ids),
        loadOwnerWithdrawals(ids),
        loadShirtsData(ids),
      ]);
    }
  };

  const handleRequestWithdraw = async () => {
    const tournament = selectedTournamentForWithdraw || selectedTournaments[0];
    if (!tournament || !withdrawAmount) return;
    setWithdrawLoading(true);
    try {
      await api.post("/managers/withdrawals", {
        tournamentId: tournament.id,
        amount: parseFloat(withdrawAmount),
        pixKey: withdrawPixKey || undefined,
      }, { headers });
      toast.success("Solicitação de saque enviada!");
      setWithdrawDialogOpen(false);
      setWithdrawAmount("");
      setWithdrawPixKey("");
      setSelectedTournamentForWithdraw(null);
      // Recarregar dados
      const ids = selectedTournaments.map((t) => t.id);
      await Promise.all([
        loadBalances(ids),
        loadOwnerWithdrawals(ids),
      ]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Erro ao solicitar saque");
    } finally {
      setWithdrawLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approveDialog) return;
    setActionLoading(true);
    try {
      await api.patch(`/administrators/withdrawals/${approveDialog.id}/approve`, {}, { headers });
      toast.success("Saque aprovado!");
      setApproveDialog(null);
      loadAdminWithdrawals();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Erro ao aprovar");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog || !rejectReason) return;
    setActionLoading(true);
    try {
      await api.patch(`/administrators/withdrawals/${rejectDialog.id}/reject`, { reason: rejectReason }, { headers });
      toast.success("Saque rejeitado.");
      setRejectDialog(null);
      setRejectReason("");
      loadAdminWithdrawals();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Erro ao rejeitar");
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmPaid = async () => {
    if (!paidDialog) return;
    setActionLoading(true);
    try {
      await api.patch(`/administrators/withdrawals/${paidDialog.id}/paid`, {}, { headers });
      toast.success("Pagamento confirmado!");
      setPaidDialog(null);
      loadAdminWithdrawals();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Erro ao confirmar pagamento");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateServiceCharge = async () => {
    const tournament = serviceChargeTournament || selectedTournaments[0];
    if (!tournament || !serviceChargeAmount || !serviceChargeDescription) return;
    setServiceChargeLoading(true);
    try {
      await api.post("/administrators/withdrawals/service-charge", {
        tournamentId: tournament.id,
        amount: parseFloat(serviceChargeAmount),
        description: serviceChargeDescription,
      }, { headers });
      toast.success("Serviço registrado com sucesso!");
      setServiceChargeDialogOpen(false);
      setServiceChargeAmount("");
      setServiceChargeDescription("");
      setServiceChargeTournament(null);
      const ids = selectedTournaments.map((t) => t.id);
      await Promise.all([loadBalances(ids), loadAdminTournamentWithdrawals(ids)]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Erro ao registrar serviço");
    } finally {
      setServiceChargeLoading(false);
    }
  };

  const calcTotal = (items: { value: string; occurrences: number }[]) =>
    items.reduce((acc, item) => acc + (parseFloat(item.value) || 0) * item.occurrences, 0);

  const formatBRL = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const highlightText = (text: string, term: string) => {
    if (!term) return text;
    const idx = text.toUpperCase().indexOf(term.toUpperCase());
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + term.length);
    const after = text.slice(idx + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "#fef08a", borderRadius: 2, padding: "0 1px" }}>{match}</span>
        {after}
      </>
    );
  };

  const styledHeaderSx = {
    color: "#fff",
    fontWeight: 700,
    fontSize: "12px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    py: 1.5,
    whiteSpace: "nowrap",
    "& .MuiTableSortLabel-root": { color: "#fff !important" },
    "& .MuiTableSortLabel-root.Mui-active": { color: "#fff !important" },
    "& .MuiTableSortLabel-icon": { color: "#fff !important", opacity: 0.7 },
  };

  const totalReceitaManual = localInfo ? calcTotal(localInfo.resultManual) : 0;
  const totalReceitaAuto = localInfo ? calcTotal(localInfo.resultAuto) : 0;
  const totalReceita = totalReceitaManual + totalReceitaAuto;

  const totalCadastroEvento = selectedTournaments.reduce((acc, t) => acc + (Number(t.valorCadastroEvento) || 0), 0);
  const totalAcompanhamento = selectedTournaments.reduce((acc, t) => acc + (Number(t.valorAcompanhamento) || 0), 0);

  const avgTaxaPlataforma = selectedTournaments.length > 0
    ? selectedTournaments.reduce((acc, t) => acc + (Number(t.taxaPlataforma) || 3), 0) / selectedTournaments.length
    : 3;
  const avgTaxaPagbank = selectedTournaments.length > 0
    ? selectedTournaments.reduce((acc, t) => acc + (Number(t.taxaPagbank) || 0.3), 0) / selectedTournaments.length
    : 0.3;

  const valorCobradoPlataforma = totalReceitaAuto * (avgTaxaPlataforma / 100);
  const valorCobradoPagbank = totalReceitaAuto * (avgTaxaPagbank / 100);
  const lucroPlataforma = valorCobradoPlataforma - valorCobradoPagbank + totalCadastroEvento + totalAcompanhamento;

  // Totais admin saques (só WITHDRAWAL, exclui SERVICE_CHARGE)
  const adminWithdrawalsOnly = allWithdrawals.filter((w) => w.type !== "SERVICE_CHARGE");
  const adminTotalByStatus = (status: string) =>
    adminWithdrawalsOnly.filter((w) => w.status === status).reduce((acc, w) => acc + Number(w.amount), 0);
  const adminServiceChargesTotal = allWithdrawals
    .filter((w) => w.type === "SERVICE_CHARGE")
    .reduce((acc, w) => acc + Number(w.amount), 0);

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <PageLocationTitle>Financeiro por Torneio</PageLocationTitle>
          {isAdmin && (
            <Button variant="outlined" size="small" onClick={() => navigate("/admin/financial-dashboard")}>
              Ver Dashboard Global
            </Button>
          )}
        </Box>

        {/* Tabs do admin */}
        {isAdmin && (
          <Tabs value={adminTab} onChange={(_, v) => setAdminTab(v)} sx={{ mb: 3 }}>
            <Tab label="Financeiro" />
            <Tab label="Solicitações de Saque" />
          </Tabs>
        )}

        {/* ========== TAB 0: FINANCEIRO ========== */}
        {(!isAdmin || adminTab === 0) && (
          <>
            <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Autocomplete
                multiple
                options={tournaments}
                getOptionLabel={(option: Tournament) => option.name}
                value={selectedTournaments}
                onChange={(_, value) => handleSelectTournaments(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Selecione o(s) Torneio(s)" variant="outlined" />
                )}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                sx={{ flex: 1 }}
              />
              {selectedTournaments.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={refreshData}
                  disabled={loading}
                  sx={{ height: 56, minWidth: 120 }}
                >
                  {loading ? <CircularProgress size={20} /> : "Atualizar"}
                </Button>
              )}
            </Box>

            {loading && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            )}

            {/* ========== VISÃO DO DONO - dados do backend ========== */}
            {!isAdmin && Object.keys(balanceMap).length > 0 && selectedTournaments.length > 0 && (() => {
              const balances = Object.values(balanceMap);
              const sumField = (field: keyof BalanceInfo) => balances.reduce((acc, b) => acc + Number(b[field]), 0);

              return (
                <>
                  {/* Cards resumo */}
                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }}
                    gap={2}
                    sx={{ mb: 3 }}
                  >
                    <SummaryCard>
                      <Box display="flex" flexDirection="column">
                        <SummaryLabel>Total Inscrições Pagas</SummaryLabel>
                        <SummaryValue translate="no">{sumField("totalPaid")}</SummaryValue>
                      </Box>
                    </SummaryCard>
                    <SummaryCard>
                      <Box display="flex" flexDirection="column">
                        <SummaryLabel>Total Inscrições Não Pagas</SummaryLabel>
                        <SummaryValue translate="no">{sumField("totalUnpaid")}</SummaryValue>
                      </Box>
                    </SummaryCard>
                    <SummaryCard>
                      <Box display="flex" flexDirection="column">
                        <SummaryLabel>Receita Total</SummaryLabel>
                        <SummaryValue translate="no" style={{ color: "#218802" }}>
                          {formatBRL(sumField("totalRevenue"))}
                        </SummaryValue>
                      </Box>
                    </SummaryCard>
                    <SummaryCard>
                      <Box display="flex" flexDirection="column">
                        <SummaryLabel>Total Inscrições</SummaryLabel>
                        <SummaryValue translate="no">
                          {sumField("totalPaid") + sumField("totalUnpaid")}
                        </SummaryValue>
                      </Box>
                    </SummaryCard>
                  </Box>

                  {/* Cards receita manual vs automatica */}
                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr" }}
                    gap={2}
                    sx={{ mb: 3 }}
                  >
                    <SummaryCard>
                      <Box display="flex" flexDirection="column">
                        <SummaryLabel>Receita - Pagamentos Manuais</SummaryLabel>
                        <SummaryValue translate="no" style={{ color: "#1a73e8" }}>
                          {formatBRL(sumField("grossManual"))}
                        </SummaryValue>
                        <Typography variant="caption" color="textSecondary">
                          <span>{sumField("countManual")}</span> pagamento(s)
                        </Typography>
                      </Box>
                    </SummaryCard>
                    <SummaryCard>
                      <Box display="flex" flexDirection="column">
                        <SummaryLabel>Receita Bruta - Pagamentos Automáticos</SummaryLabel>
                        <SummaryValue translate="no" style={{ color: "#1a73e8" }}>
                          {formatBRL(sumField("grossAuto"))}
                        </SummaryValue>
                        <Typography variant="caption" color="textSecondary">
                          <span>{sumField("countAuto")}</span> pagamento(s)
                        </Typography>
                      </Box>
                    </SummaryCard>
                    <SummaryCard>
                      <Box display="flex" flexDirection="column">
                        <SummaryLabel>Receita Líquida - Pagamentos Automáticos</SummaryLabel>
                        <SummaryValue translate="no" style={{ color: "#218802" }}>
                          {formatBRL(sumField("netRevenue"))}
                        </SummaryValue>
                        <Typography variant="caption" color="textSecondary" translate="no">
                          Bruto - taxa plataforma ({formatBRL(sumField("feeAmount"))})
                        </Typography>
                      </Box>
                    </SummaryCard>
                  </Box>
                </>
              );
            })()}

            {/* ========== VISÃO DO DONO - CARDS DE SALDO ========== */}
            {!isAdmin && Object.keys(balanceMap).length > 0 && selectedTournaments.length > 0 && (() => {
                  const balances = Object.values(balanceMap);
                  const totalNet = balances.reduce((acc, b) => acc + b.netRevenue, 0);
                  const totalPaid = balances.reduce((acc, b) => acc + b.alreadyPaid, 0);
                  const totalPending = balances.reduce((acc, b) => acc + b.pendingAmount, 0);
                  const totalServiceCharges = balances.reduce((acc, b) => acc + (b.serviceCharges || 0), 0);
                  const totalAvailable = balances.reduce((acc, b) => acc + b.availableBalance, 0);

                  return (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Saldo para Saque {selectedTournaments.length > 1 ? "(Consolidado)" : ""}
                      </Typography>
                      <Box
                        display="grid"
                        gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr 1fr" }}
                        gap={2}
                        sx={{ mb: 2 }}
                      >
                        <SummaryCard>
                          <Box display="flex" flexDirection="column">
                            <SummaryLabel>Receita Líquida</SummaryLabel>
                            <SummaryValue translate="no" style={{ color: "#218802" }}>
                              {formatBRL(totalNet)}
                            </SummaryValue>
                            <Typography variant="caption" color="textSecondary">
                              {selectedTournaments.length > 1 ? `${selectedTournaments.length} torneios` : `Bruto - ${balances[0]?.taxaPlataforma}% de taxa`}
                            </Typography>
                          </Box>
                        </SummaryCard>
                        <SummaryCard>
                          <Box display="flex" flexDirection="column">
                            <SummaryLabel>Já Sacado</SummaryLabel>
                            <SummaryValue translate="no" style={{ color: "#1a73e8" }}>
                              {formatBRL(totalPaid)}
                            </SummaryValue>
                            <Typography variant="caption" color="textSecondary">
                              Saques já pagos
                            </Typography>
                          </Box>
                        </SummaryCard>
                        <SummaryCard>
                          <Box display="flex" flexDirection="column">
                            <SummaryLabel>Saque Pendente</SummaryLabel>
                            <SummaryValue translate="no" style={{ color: "#ed6c02" }}>
                              {formatBRL(totalPending)}
                            </SummaryValue>
                            <Typography variant="caption" color="textSecondary">
                              Aguardando aprovação/pagamento
                            </Typography>
                          </Box>
                        </SummaryCard>
                        {totalServiceCharges > 0 && (
                        <SummaryCard>
                          <Box display="flex" flexDirection="column">
                            <SummaryLabel>Serviços</SummaryLabel>
                            <SummaryValue translate="no" style={{ color: "#9c27b0" }}>
                              {formatBRL(totalServiceCharges)}
                            </SummaryValue>
                            <Typography variant="caption" color="textSecondary">
                              Cobranças de serviço
                            </Typography>
                          </Box>
                        </SummaryCard>
                        )}
                        <SummaryCard>
                          <Box display="flex" flexDirection="column">
                            <SummaryLabel>Saldo Disponível</SummaryLabel>
                            <SummaryValue translate="no" style={{ color: "#218802" }}>
                              {formatBRL(totalAvailable)}
                            </SummaryValue>
                            {selectedTournaments.length === 1 && (
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ mt: 1 }}
                                disabled={totalAvailable <= 0}
                                onClick={() => {
                                  setSelectedTournamentForWithdraw(selectedTournaments[0]);
                                  setWithdrawAmount(totalAvailable.toFixed(2));
                                  setWithdrawDialogOpen(true);
                                }}
                              >
                                Solicitar Saque
                              </Button>
                            )}
                          </Box>
                        </SummaryCard>
                      </Box>

                      {/* Detalhamento por torneio quando múltiplos selecionados */}
                      {selectedTournaments.length > 1 && (
                        <TableContainer sx={{ mb: 3, borderRadius: "12px", border: "1px solid rgba(26,34,82,0.06)", overflow: "auto" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ "& th": { backgroundColor: "#1a2252" } }}>
                                {["Torneio", "Receita Líquida", "Já Sacado", "Pendente", "Serviços", "Disponível", "Ação"].map((label) => (
                                  <TableCell key={label} sx={styledHeaderSx}>{label}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedTournaments.map((t, index) => {
                                const b = balanceMap[t.id];
                                if (!b) return null;
                                return (
                                  <TableRow key={t.id} sx={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f7f8fb", "&:hover": { backgroundColor: "#f0f2f8" } }}>
                                    <TableCell sx={{ fontWeight: 600, fontSize: "13px", color: "#1a2252" }}>{t.name}</TableCell>
                                    <TableCell translate="no" sx={{ fontSize: "13px" }}>{formatBRL(b.netRevenue)}</TableCell>
                                    <TableCell translate="no" sx={{ fontSize: "13px" }}>{formatBRL(b.alreadyPaid)}</TableCell>
                                    <TableCell translate="no" sx={{ fontSize: "13px" }}>{formatBRL(b.pendingAmount)}</TableCell>
                                    <TableCell translate="no" sx={{ fontSize: "13px", color: "#9c27b0" }}>{formatBRL(b.serviceCharges || 0)}</TableCell>
                                    <TableCell translate="no" sx={{ fontSize: "13px", color: "#218802", fontWeight: 600 }}>
                                      {formatBRL(b.availableBalance)}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={b.availableBalance <= 0}
                                        onClick={() => {
                                          setSelectedTournamentForWithdraw(t);
                                          setWithdrawAmount(b.availableBalance.toFixed(2));
                                          setWithdrawDialogOpen(true);
                                        }}
                                      >
                                        Solicitar Saque
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </>
                  );
                })()}

                {/* Tabela de saques do dono */}
                {!isAdmin && ownerWithdrawals.length > 0 && (() => {
                  const handleOwnerWdSort = (field: string) => {
                    if (ownerWithdrawalsSortField === field) {
                      setOwnerWithdrawalsSortDir(ownerWithdrawalsSortDir === "asc" ? "desc" : "asc");
                    } else {
                      setOwnerWithdrawalsSortField(field);
                      setOwnerWithdrawalsSortDir("asc");
                    }
                  };

                  let filteredOwnerWd = ownerWithdrawals.filter((w) => {
                    if (!ownerWithdrawalsSearch) return true;
                    const term = ownerWithdrawalsSearch.toUpperCase();
                    return (
                      w.tournament.name.toUpperCase().includes(term) ||
                      (w.pixKey || "").toUpperCase().includes(term) ||
                      (w.description || "").toUpperCase().includes(term) ||
                      (statusConfig[w.status]?.label || w.status).toUpperCase().includes(term)
                    );
                  });

                  if (ownerWithdrawalsSortField) {
                    filteredOwnerWd = [...filteredOwnerWd].sort((a, b) => {
                      const dir = ownerWithdrawalsSortDir === "asc" ? 1 : -1;
                      switch (ownerWithdrawalsSortField) {
                        case "requestedAt": return (new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime()) * dir;
                        case "type": return a.type.localeCompare(b.type) * dir;
                        case "tournament": return a.tournament.name.localeCompare(b.tournament.name, "pt-BR") * dir;
                        case "amount": return (Number(a.amount) - Number(b.amount)) * dir;
                        case "status": return a.status.localeCompare(b.status) * dir;
                        case "paidAt": return ((a.paidAt || "").localeCompare(b.paidAt || "")) * dir;
                        default: return 0;
                      }
                    });
                  }

                  const exportOwnerWdXLSX = () => {
                    const wsData = filteredOwnerWd.map((w, i) => ({
                      "#": i + 1,
                      Data: formatDate(w.requestedAt),
                      Tipo: w.type === "SERVICE_CHARGE" ? "Serviço" : "Saque",
                      Torneio: w.tournament.name,
                      Valor: Number(w.amount),
                      PIX: w.pixKey || "-",
                      "Descrição": w.description || "-",
                      Status: statusConfig[w.status]?.label || w.status,
                      "Pago em": formatDate(w.paidAt),
                    }));
                    const ws = XLSX.utils.json_to_sheet(wsData);
                    ws["!cols"] = [{ wch: 5 }, { wch: 18 }, { wch: 10 }, { wch: 30 }, { wch: 12 }, { wch: 25 }, { wch: 30 }, { wch: 12 }, { wch: 18 }];
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Movimentações");
                    XLSX.writeFile(wb, "historico-movimentacoes.xlsx");
                  };

                  const ownerWdColumns = [
                    { label: "#", field: null, align: "center" as const },
                    { label: "Data", field: "requestedAt", align: "left" as const },
                    { label: "Tipo", field: "type", align: "center" as const },
                    { label: "Torneio", field: "tournament", align: "left" as const },
                    { label: "Valor", field: "amount", align: "right" as const },
                    { label: "PIX", field: null, align: "left" as const },
                    { label: "Descrição", field: null, align: "left" as const },
                    { label: "Status", field: "status", align: "center" as const },
                    { label: "Pago em", field: "paidAt", align: "left" as const },
                  ];

                  return (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Histórico de Movimentações (<span>{filteredOwnerWd.length}</span>)
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                        <TextField
                          size="small"
                          placeholder="Buscar por torneio, PIX, descrição, status..."
                          value={ownerWithdrawalsSearch}
                          onChange={(e) => setOwnerWithdrawalsSearch(e.target.value)}
                          sx={{ flex: 1, minWidth: 200, backgroundColor: "#fff", borderRadius: "8px", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                        />
                        <Tooltip title="Exportar para Excel">
                          <Button
                            variant="outlined"
                            onClick={exportOwnerWdXLSX}
                            sx={{ borderColor: "#1a2252", color: "#1a2252", textTransform: "none", fontWeight: 600, borderRadius: "8px", "&:hover": { backgroundColor: "#1a2252", color: "#fff" } }}
                          >
                            Exportar XLSX
                          </Button>
                        </Tooltip>
                      </Box>
                      <TableContainer sx={{ mb: 3, borderRadius: "12px", border: "1px solid rgba(26,34,82,0.06)", overflow: "auto", maxHeight: 500 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow sx={{ "& th": { backgroundColor: "#1a2252" } }}>
                              {ownerWdColumns.map((col) => (
                                <TableCell key={col.label} align={col.align} sx={styledHeaderSx}>
                                  {col.field ? (
                                    <TableSortLabel active={ownerWithdrawalsSortField === col.field} direction={ownerWithdrawalsSortField === col.field ? ownerWithdrawalsSortDir : "asc"} onClick={() => handleOwnerWdSort(col.field!)}>
                                      {col.label}
                                    </TableSortLabel>
                                  ) : col.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredOwnerWd.length > 0 ? filteredOwnerWd.map((w, index) => (
                              <TableRow key={w.id} sx={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f7f8fb", "&:hover": { backgroundColor: "#f0f2f8" } }}>
                                <TableCell align="center" sx={{ color: "#6b7490", fontSize: "13px", width: 40 }}>{index + 1}</TableCell>
                                <TableCell sx={{ fontSize: "13px", color: "#353d58", whiteSpace: "nowrap" }}>{formatDate(w.requestedAt)}</TableCell>
                                <TableCell align="center">
                                  <Chip label={w.type === "SERVICE_CHARGE" ? "Serviço" : "Saque"} size="small" color={w.type === "SERVICE_CHARGE" ? "secondary" : "default"} variant="outlined" />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: "13px", color: "#1a2252" }}>{highlightText(w.tournament.name, ownerWithdrawalsSearch)}</TableCell>
                                <TableCell align="right" translate="no" sx={{ whiteSpace: "nowrap" }}>
                                  <Chip label={formatBRL(Number(w.amount))} size="small" sx={{ fontWeight: 700, fontSize: "12px", backgroundColor: "#dcfce7", color: "#166534" }} />
                                </TableCell>
                                <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>{highlightText(w.pixKey || "-", ownerWithdrawalsSearch)}</TableCell>
                                <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>{highlightText(w.description || "-", ownerWithdrawalsSearch)}</TableCell>
                                <TableCell align="center">
                                  <Tooltip title={w.rejectionReason || ""} arrow disableHoverListener={!w.rejectionReason}>
                                    <Chip label={statusConfig[w.status]?.label || w.status} color={statusConfig[w.status]?.color || "default"} size="small" />
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{ fontSize: "13px", color: "#353d58", whiteSpace: "nowrap" }}>{formatDate(w.paidAt)}</TableCell>
                              </TableRow>
                            )) : (
                              <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4, color: "#9098b0" }}>
                                  {ownerWithdrawalsSearch ? "Nenhum resultado para a busca" : "Nenhum dado encontrado"}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  );
                })()}

                {/* ========== VISÃO DO ADMIN - usa localInfo ========== */}
                {isAdmin && localInfo && selectedTournaments.length > 0 && (
                  <>
                    {/* Cards resumo admin */}
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }}
                      gap={2}
                      sx={{ mb: 3 }}
                    >
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Total Inscrições Pagas</SummaryLabel>
                          <SummaryValue translate="no">{localInfo.totalPaid}</SummaryValue>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Total Inscrições Não Pagas</SummaryLabel>
                          <SummaryValue translate="no">{localInfo.totalUnpaid}</SummaryValue>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Receita Total</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#218802" }}>
                            {formatBRL(totalReceita)}
                          </SummaryValue>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Total Inscrições</SummaryLabel>
                          <SummaryValue translate="no">
                            {localInfo.totalPaid + localInfo.totalUnpaid}
                          </SummaryValue>
                        </Box>
                      </SummaryCard>
                    </Box>

                    {/* Cards receita manual vs automatica admin */}
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr" }}
                      gap={2}
                      sx={{ mb: 3 }}
                    >
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Receita - Pagamentos Manuais</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#1a73e8" }}>
                            {formatBRL(totalReceitaManual)}
                          </SummaryValue>
                          <Typography variant="caption" color="textSecondary">
                            {localInfo.resultManual.reduce((acc, i) => acc + i.occurrences, 0)} pagamento(s)
                          </Typography>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Receita Bruta - Pagamentos Automáticos</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#1a73e8" }}>
                            {formatBRL(totalReceitaAuto)}
                          </SummaryValue>
                          <Typography variant="caption" color="textSecondary">
                            {localInfo.resultAuto.reduce((acc, i) => acc + i.occurrences, 0)} pagamento(s)
                          </Typography>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Receita Líquida - Pagamentos Automáticos</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#218802" }}>
                            {formatBRL(totalReceitaAuto - valorCobradoPlataforma)}
                          </SummaryValue>
                          <Typography variant="caption" color="textSecondary" translate="no">
                            Bruto - {avgTaxaPlataforma.toFixed(1)}% ({formatBRL(valorCobradoPlataforma)})
                          </Typography>
                        </Box>
                      </SummaryCard>
                    </Box>

                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Lucro da Plataforma (Pagamentos Automáticos)
                    </Typography>
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }}
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Taxa cobrada do cliente</SummaryLabel>
                          <SummaryValue translate="no">{avgTaxaPlataforma.toFixed(1)}%</SummaryValue>
                          <Typography variant="caption" color="textSecondary" translate="no">
                            {formatBRL(valorCobradoPlataforma)} sobre {formatBRL(totalReceitaAuto)}
                          </Typography>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Taxa cobrada pelo PagBank</SummaryLabel>
                          <SummaryValue translate="no">{avgTaxaPagbank.toFixed(1)}%</SummaryValue>
                          <Typography variant="caption" color="textSecondary" translate="no">
                            -{formatBRL(valorCobradoPagbank)} sobre {formatBRL(totalReceitaAuto)}
                          </Typography>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Cadastro do evento</SummaryLabel>
                          <SummaryValue translate="no">{formatBRL(totalCadastroEvento)}</SummaryValue>
                          <Typography variant="caption" color="textSecondary">
                            Somado ao lucro da plataforma
                          </Typography>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Acompanhamento no evento</SummaryLabel>
                          <SummaryValue translate="no">{formatBRL(totalAcompanhamento)}</SummaryValue>
                          <Typography variant="caption" color="textSecondary">
                            Somado ao lucro da plataforma
                          </Typography>
                        </Box>
                      </SummaryCard>
                    </Box>
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr" }}
                      gap={2}
                      sx={{ mb: 3 }}
                    >
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Cobrado dos clientes</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#1a73e8" }}>
                            {formatBRL(valorCobradoPlataforma)}
                          </SummaryValue>
                          <Typography variant="caption" color="textSecondary" translate="no">
                            {avgTaxaPlataforma.toFixed(1)}% da receita automática
                          </Typography>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Pago ao PagBank</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#ac0000" }}>
                            -{formatBRL(valorCobradoPagbank)}
                          </SummaryValue>
                          <Typography variant="caption" color="textSecondary" translate="no">
                            {avgTaxaPagbank.toFixed(1)}% da receita automática
                          </Typography>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Lucro da Plataforma</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#218802" }}>
                            {formatBRL(lucroPlataforma)}
                          </SummaryValue>
                          <Typography variant="caption" color="textSecondary" translate="no">
                            {formatBRL(valorCobradoPlataforma)} - {formatBRL(valorCobradoPagbank)}
                            {totalCadastroEvento > 0 ? ` + ${formatBRL(totalCadastroEvento)}` : ""}
                            {totalAcompanhamento > 0 ? ` + ${formatBRL(totalAcompanhamento)}` : ""}
                          </Typography>
                        </Box>
                      </SummaryCard>
                    </Box>

                    {/* ========== ADMIN - CARDS DE SALDO POR TORNEIO ========== */}
                    {Object.keys(balanceMap).length > 0 && (() => {
                      const balances = Object.values(balanceMap);
                      const totalNet = balances.reduce((acc, b) => acc + b.netRevenue, 0);
                      const totalPaid = balances.reduce((acc, b) => acc + b.alreadyPaid, 0);
                      const totalPending = balances.reduce((acc, b) => acc + b.pendingAmount, 0);
                      const totalServiceCharges = balances.reduce((acc, b) => acc + (b.serviceCharges || 0), 0);
                      const totalAvailable = balances.reduce((acc, b) => acc + b.availableBalance, 0);

                      return (
                        <>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            Saldo do Organizador {selectedTournaments.length > 1 ? "(Consolidado)" : ""}
                          </Typography>
                          <Box
                            display="grid"
                            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr 1fr" }}
                            gap={2}
                            sx={{ mb: 2 }}
                          >
                            <SummaryCard>
                              <Box display="flex" flexDirection="column">
                                <SummaryLabel>Receita Líquida</SummaryLabel>
                                <SummaryValue translate="no" style={{ color: "#218802" }}>
                                  {formatBRL(totalNet)}
                                </SummaryValue>
                              </Box>
                            </SummaryCard>
                            <SummaryCard>
                              <Box display="flex" flexDirection="column">
                                <SummaryLabel>Já Sacado</SummaryLabel>
                                <SummaryValue translate="no" style={{ color: "#1a73e8" }}>
                                  {formatBRL(totalPaid)}
                                </SummaryValue>
                              </Box>
                            </SummaryCard>
                            <SummaryCard>
                              <Box display="flex" flexDirection="column">
                                <SummaryLabel>Saque Pendente</SummaryLabel>
                                <SummaryValue translate="no" style={{ color: "#ed6c02" }}>
                                  {formatBRL(totalPending)}
                                </SummaryValue>
                              </Box>
                            </SummaryCard>
                            <SummaryCard>
                              <Box display="flex" flexDirection="column">
                                <SummaryLabel>Serviços</SummaryLabel>
                                <SummaryValue translate="no" style={{ color: "#9c27b0" }}>
                                  {formatBRL(totalServiceCharges)}
                                </SummaryValue>
                              </Box>
                            </SummaryCard>
                            <SummaryCard>
                              <Box display="flex" flexDirection="column">
                                <SummaryLabel>Saldo Disponível</SummaryLabel>
                                <SummaryValue translate="no" style={{ color: "#218802" }}>
                                  {formatBRL(totalAvailable)}
                                </SummaryValue>
                              </Box>
                            </SummaryCard>
                          </Box>

                          {/* Detalhamento por torneio com botão de serviço */}
                          {selectedTournaments.length > 1 && (
                            <TableContainer sx={{ mb: 3, borderRadius: "12px", border: "1px solid rgba(26,34,82,0.06)", overflow: "auto" }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ "& th": { backgroundColor: "#1a2252" } }}>
                                    {["Torneio", "Receita Líq.", "Já Sacado", "Pendente", "Serviços", "Disponível", "Ação"].map((label) => (
                                      <TableCell key={label} sx={styledHeaderSx}>{label}</TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {selectedTournaments.map((t, index) => {
                                    const b = balanceMap[t.id];
                                    if (!b) return null;
                                    return (
                                      <TableRow key={t.id} sx={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f7f8fb", "&:hover": { backgroundColor: "#f0f2f8" } }}>
                                        <TableCell sx={{ fontWeight: 600, fontSize: "13px", color: "#1a2252" }}>{t.name}</TableCell>
                                        <TableCell translate="no" sx={{ fontSize: "13px" }}>{formatBRL(b.netRevenue)}</TableCell>
                                        <TableCell translate="no" sx={{ fontSize: "13px" }}>{formatBRL(b.alreadyPaid)}</TableCell>
                                        <TableCell translate="no" sx={{ fontSize: "13px" }}>{formatBRL(b.pendingAmount)}</TableCell>
                                        <TableCell translate="no" sx={{ fontSize: "13px", color: "#9c27b0" }}>{formatBRL(b.serviceCharges || 0)}</TableCell>
                                        <TableCell translate="no" sx={{ fontSize: "13px", color: "#218802", fontWeight: 600 }}>
                                          {formatBRL(b.availableBalance)}
                                        </TableCell>
                                        <TableCell>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => {
                                              setServiceChargeTournament(t);
                                              setServiceChargeDialogOpen(true);
                                            }}
                                          >
                                            + Serviço
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}

                          {/* Botão serviço quando 1 torneio selecionado */}
                          {selectedTournaments.length === 1 && (
                            <Box sx={{ mb: 3 }}>
                              <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => {
                                  setServiceChargeTournament(selectedTournaments[0]);
                                  setServiceChargeDialogOpen(true);
                                }}
                              >
                                + Registrar Serviço
                              </Button>
                            </Box>
                          )}
                        </>
                      );
                    })()}

                    {/* Histórico de saques dos torneios selecionados */}
                    {adminTournamentWithdrawals.length > 0 && (() => {
                      const handleAdminTWdSort = (field: string) => {
                        if (adminTournamentWdSortField === field) {
                          setAdminTournamentWdSortDir(adminTournamentWdSortDir === "asc" ? "desc" : "asc");
                        } else {
                          setAdminTournamentWdSortField(field);
                          setAdminTournamentWdSortDir("asc");
                        }
                      };

                      let filteredAdminTWd = adminTournamentWithdrawals.filter((w) => {
                        if (!adminTournamentWdSearch) return true;
                        const term = adminTournamentWdSearch.toUpperCase();
                        return (
                          w.tournament.name.toUpperCase().includes(term) ||
                          (w.requester?.name || "").toUpperCase().includes(term) ||
                          (w.pixKey || "").toUpperCase().includes(term) ||
                          (w.description || "").toUpperCase().includes(term) ||
                          (statusConfig[w.status]?.label || w.status).toUpperCase().includes(term)
                        );
                      });

                      if (adminTournamentWdSortField) {
                        filteredAdminTWd = [...filteredAdminTWd].sort((a, b) => {
                          const dir = adminTournamentWdSortDir === "asc" ? 1 : -1;
                          switch (adminTournamentWdSortField) {
                            case "requestedAt": return (new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime()) * dir;
                            case "type": return a.type.localeCompare(b.type) * dir;
                            case "tournament": return a.tournament.name.localeCompare(b.tournament.name, "pt-BR") * dir;
                            case "requester": return (a.requester?.name || "").localeCompare(b.requester?.name || "", "pt-BR") * dir;
                            case "amount": return (Number(a.amount) - Number(b.amount)) * dir;
                            case "status": return a.status.localeCompare(b.status) * dir;
                            case "paidAt": return ((a.paidAt || "").localeCompare(b.paidAt || "")) * dir;
                            default: return 0;
                          }
                        });
                      }

                      const exportAdminTWdXLSX = () => {
                        const wsData = filteredAdminTWd.map((w, i) => ({
                          "#": i + 1,
                          Data: formatDate(w.requestedAt),
                          Tipo: w.type === "SERVICE_CHARGE" ? "Serviço" : "Saque",
                          Torneio: w.tournament.name,
                          Organizador: w.requester?.name || "-",
                          Valor: Number(w.amount),
                          PIX: w.pixKey || "-",
                          "Descrição": w.description || "-",
                          Status: statusConfig[w.status]?.label || w.status,
                          "Pago em": formatDate(w.paidAt),
                        }));
                        const ws = XLSX.utils.json_to_sheet(wsData);
                        ws["!cols"] = [{ wch: 5 }, { wch: 18 }, { wch: 10 }, { wch: 30 }, { wch: 25 }, { wch: 12 }, { wch: 25 }, { wch: 30 }, { wch: 12 }, { wch: 18 }];
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, "Movimentações Admin");
                        XLSX.writeFile(wb, "historico-movimentacoes-admin.xlsx");
                      };

                      const adminTWdColumns = [
                        { label: "#", field: null, align: "center" as const },
                        { label: "Data", field: "requestedAt", align: "left" as const },
                        { label: "Tipo", field: "type", align: "center" as const },
                        { label: "Torneio", field: "tournament", align: "left" as const },
                        { label: "Organizador", field: "requester", align: "left" as const },
                        { label: "Valor", field: "amount", align: "right" as const },
                        { label: "PIX", field: null, align: "left" as const },
                        { label: "Descrição", field: null, align: "left" as const },
                        { label: "Status", field: "status", align: "center" as const },
                        { label: "Pago em", field: "paidAt", align: "left" as const },
                      ];

                      return (
                        <>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            Histórico de Movimentações (<span>{filteredAdminTWd.length}</span>)
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                            <TextField
                              size="small"
                              placeholder="Buscar por torneio, organizador, PIX, descrição..."
                              value={adminTournamentWdSearch}
                              onChange={(e) => setAdminTournamentWdSearch(e.target.value)}
                              sx={{ flex: 1, minWidth: 200, backgroundColor: "#fff", borderRadius: "8px", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                            />
                            <Tooltip title="Exportar para Excel">
                              <Button
                                variant="outlined"
                                onClick={exportAdminTWdXLSX}
                                sx={{ borderColor: "#1a2252", color: "#1a2252", textTransform: "none", fontWeight: 600, borderRadius: "8px", "&:hover": { backgroundColor: "#1a2252", color: "#fff" } }}
                              >
                                Exportar XLSX
                              </Button>
                            </Tooltip>
                          </Box>
                          <TableContainer sx={{ mb: 3, borderRadius: "12px", border: "1px solid rgba(26,34,82,0.06)", overflow: "auto", maxHeight: 500 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow sx={{ "& th": { backgroundColor: "#1a2252" } }}>
                                  {adminTWdColumns.map((col) => (
                                    <TableCell key={col.label} align={col.align} sx={styledHeaderSx}>
                                      {col.field ? (
                                        <TableSortLabel active={adminTournamentWdSortField === col.field} direction={adminTournamentWdSortField === col.field ? adminTournamentWdSortDir : "asc"} onClick={() => handleAdminTWdSort(col.field!)}>
                                          {col.label}
                                        </TableSortLabel>
                                      ) : col.label}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredAdminTWd.length > 0 ? filteredAdminTWd.map((w, index) => (
                                  <TableRow key={w.id} sx={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f7f8fb", "&:hover": { backgroundColor: "#f0f2f8" } }}>
                                    <TableCell align="center" sx={{ color: "#6b7490", fontSize: "13px", width: 40 }}>{index + 1}</TableCell>
                                    <TableCell sx={{ fontSize: "13px", color: "#353d58", whiteSpace: "nowrap" }}>{formatDate(w.requestedAt)}</TableCell>
                                    <TableCell align="center">
                                      <Chip label={w.type === "SERVICE_CHARGE" ? "Serviço" : "Saque"} size="small" color={w.type === "SERVICE_CHARGE" ? "secondary" : "default"} variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: "13px", color: "#1a2252" }}>{highlightText(w.tournament.name, adminTournamentWdSearch)}</TableCell>
                                    <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>{highlightText(w.requester?.name || "-", adminTournamentWdSearch)}</TableCell>
                                    <TableCell align="right" translate="no" sx={{ whiteSpace: "nowrap" }}>
                                      <Chip label={formatBRL(Number(w.amount))} size="small" sx={{ fontWeight: 700, fontSize: "12px", backgroundColor: "#dcfce7", color: "#166534" }} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>{highlightText(w.pixKey || "-", adminTournamentWdSearch)}</TableCell>
                                    <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>{highlightText(w.description || "-", adminTournamentWdSearch)}</TableCell>
                                    <TableCell align="center">
                                      <Chip label={statusConfig[w.status]?.label || w.status} color={statusConfig[w.status]?.color || "default"} size="small" />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: "13px", color: "#353d58", whiteSpace: "nowrap" }}>{formatDate(w.paidAt)}</TableCell>
                                  </TableRow>
                                )) : (
                                  <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: "#9098b0" }}>
                                      {adminTournamentWdSearch ? "Nenhum resultado para a busca" : "Nenhum dado encontrado"}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      );
                    })()}

                    {/* Detalhes agrupados por valor */}
                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} sx={{ mb: 3 }}>
                      {localInfo.resultManual.length > 0 && (
                        <SummaryCard>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Detalhamento - Manuais
                          </Typography>
                          {localInfo.resultManual.map((item, idx) => (
                            <Typography key={idx} variant="body2" translate="no">
                              <span>{item.occurrences}x</span> - {formatBRL(parseFloat(item.value))} ={" "}
                              <strong>{formatBRL(parseFloat(item.value) * item.occurrences)}</strong>
                            </Typography>
                          ))}
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 700, borderTop: "1px solid #eee", pt: 1 }} translate="no">
                            Total: {formatBRL(totalReceitaManual)}
                          </Typography>
                        </SummaryCard>
                      )}
                      {localInfo.resultAuto.length > 0 && (
                        <SummaryCard>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Detalhamento - Automáticos
                          </Typography>
                          {localInfo.resultAuto.map((item, idx) => (
                            <Typography key={idx} variant="body2" translate="no">
                              <span>{item.occurrences}x</span> - {formatBRL(parseFloat(item.value))} ={" "}
                              <strong>{formatBRL(parseFloat(item.value) * item.occurrences)}</strong>
                            </Typography>
                          ))}
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 700, borderTop: "1px solid #eee", pt: 1 }} translate="no">
                            Total: {formatBRL(totalReceitaAuto)}
                          </Typography>
                        </SummaryCard>
                      )}
                    </Box>
                  </>
                )}

                {/* ========== SEÇÃO DE CAMISAS ========== */}
                {shirtsData && (shirtsData.paid.length > 0 || shirtsData.pending.length > 0) && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Receita de Camisas
                    </Typography>
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr 1fr" }}
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Camisas Pagas</SummaryLabel>
                          <SummaryValue translate="no">
                            {shirtsData.paid.reduce((acc, i) => acc + i.quantity, 0)}
                          </SummaryValue>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Receita Camisas (Pagas)</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#218802" }}>
                            {formatBRL(shirtsData.paid.reduce((acc, i) => acc + i.totalPrice, 0))}
                          </SummaryValue>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Camisas Pendentes</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#ed6c02" }}>
                            {shirtsData.pending.reduce((acc, i) => acc + i.quantity, 0)}
                          </SummaryValue>
                        </Box>
                      </SummaryCard>
                      <SummaryCard>
                        <Box display="flex" flexDirection="column">
                          <SummaryLabel>Receita Camisas (Pendentes)</SummaryLabel>
                          <SummaryValue translate="no" style={{ color: "#ed6c02" }}>
                            {formatBRL(shirtsData.pending.reduce((acc, i) => acc + i.totalPrice, 0))}
                          </SummaryValue>
                        </Box>
                      </SummaryCard>
                    </Box>

                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} sx={{ mb: 3 }}>
                      {shirtsData.paid.length > 0 && (
                        <SummaryCard>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Detalhamento - Camisas Pagas
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Tamanho</TableCell>
                                  <TableCell align="right">Valor Unit.</TableCell>
                                  <TableCell align="right">Qtd</TableCell>
                                  <TableCell align="right">Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {shirtsData.paid
                                  .slice()
                                  .sort((a, b) => a.size.localeCompare(b.size, "pt-BR") || a.price - b.price)
                                  .map((item, idx) => (
                                    <TableRow key={`${item.size}-${item.price}-${idx}`}>
                                      <TableCell>{item.size}</TableCell>
                                      <TableCell align="right" translate="no">{formatBRL(item.price)}</TableCell>
                                      <TableCell align="right" translate="no">{item.quantity}</TableCell>
                                      <TableCell align="right" translate="no">{formatBRL(item.totalPrice)}</TableCell>
                                    </TableRow>
                                  ))}
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700, borderTop: "2px solid #ddd" }} colSpan={2}>Total</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, borderTop: "2px solid #ddd" }} translate="no">
                                    {shirtsData.paid.reduce((acc, i) => acc + i.quantity, 0)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, borderTop: "2px solid #ddd" }} translate="no">
                                    {formatBRL(shirtsData.paid.reduce((acc, i) => acc + i.totalPrice, 0))}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </SummaryCard>
                      )}
                      {shirtsData.pending.length > 0 && (
                        <SummaryCard>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Detalhamento - Camisas Pendentes
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Tamanho</TableCell>
                                  <TableCell align="right">Valor Unit.</TableCell>
                                  <TableCell align="right">Qtd</TableCell>
                                  <TableCell align="right">Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {shirtsData.pending
                                  .slice()
                                  .sort((a, b) => a.size.localeCompare(b.size, "pt-BR") || a.price - b.price)
                                  .map((item, idx) => (
                                    <TableRow key={`${item.size}-${item.price}-${idx}`}>
                                      <TableCell>{item.size}</TableCell>
                                      <TableCell align="right" translate="no">{formatBRL(item.price)}</TableCell>
                                      <TableCell align="right" translate="no">{item.quantity}</TableCell>
                                      <TableCell align="right" translate="no">{formatBRL(item.totalPrice)}</TableCell>
                                    </TableRow>
                                  ))}
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700, borderTop: "2px solid #ddd" }} colSpan={2}>Total</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, borderTop: "2px solid #ddd" }} translate="no">
                                    {shirtsData.pending.reduce((acc, i) => acc + i.quantity, 0)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, borderTop: "2px solid #ddd" }} translate="no">
                                    {formatBRL(shirtsData.pending.reduce((acc, i) => acc + i.totalPrice, 0))}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </SummaryCard>
                      )}
                    </Box>

                    {shirtsData.details && shirtsData.details.length > 0 && (() => {
                      const statusLabel = { paid: "Pago", pending: "Pendente", orphan: "Sem inscr." } as const;
                      const statusColor = { paid: "success", pending: "warning", orphan: "error" } as const;
                      const paymentTypeLabel = { auto: "Automatico", manual: "Manual" } as const;

                      const handleShirtsSort = (field: string) => {
                        if (shirtsSortField === field) {
                          setShirtsSortDir(shirtsSortDir === "asc" ? "desc" : "asc");
                        } else {
                          setShirtsSortField(field);
                          setShirtsSortDir("asc");
                        }
                      };

                      let filtered = shirtsFilter === "all"
                        ? shirtsData.details
                        : shirtsData.details.filter((d) => d.status === shirtsFilter);

                      if (shirtsSearch) {
                        const term = shirtsSearch.toUpperCase();
                        filtered = filtered.filter((d) =>
                          d.athleteName.toUpperCase().includes(term) ||
                          d.size.toUpperCase().includes(term) ||
                          d.measure.toUpperCase().includes(term) ||
                          String(d.subscriptionId ?? "").includes(term) ||
                          String(d.tshirtId).includes(term)
                        );
                      }

                      if (shirtsSortField) {
                        filtered = [...filtered].sort((a, b) => {
                          const dir = shirtsSortDir === "asc" ? 1 : -1;
                          switch (shirtsSortField) {
                            case "subscriptionId": return ((a.subscriptionId ?? 0) - (b.subscriptionId ?? 0)) * dir;
                            case "tshirtId": return (a.tshirtId - b.tshirtId) * dir;
                            case "athleteName": return a.athleteName.localeCompare(b.athleteName, "pt-BR") * dir;
                            case "measure": return a.measure.localeCompare(b.measure, "pt-BR") * dir;
                            case "size": return a.size.localeCompare(b.size, "pt-BR") * dir;
                            case "price": return (a.price - b.price) * dir;
                            case "status": return a.status.localeCompare(b.status) * dir;
                            case "paymentType": return ((a.paymentType ?? "").localeCompare(b.paymentType ?? "")) * dir;
                            default: return 0;
                          }
                        });
                      }

                      const exportShirtsXLSX = () => {
                        const wsData = filtered.map((d, i) => ({
                          "#": i + 1,
                          "ID Inscr.": d.subscriptionId ?? "-",
                          "ID Camisa": d.tshirtId,
                          Atleta: d.athleteName,
                          "Descr.": d.measure,
                          Tamanho: d.size,
                          Valor: d.price,
                          Status: statusLabel[d.status],
                          "Tipo Pgto": d.paymentType ? paymentTypeLabel[d.paymentType] : "-",
                        }));

                        const ws = XLSX.utils.json_to_sheet(wsData);
                        ws["!cols"] = [
                          { wch: 5 }, { wch: 10 }, { wch: 10 }, { wch: 30 }, { wch: 20 },
                          { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
                        ];
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, "Camisas");
                        XLSX.writeFile(wb, "detalhamento-camisas.xlsx");
                      };

                      const shirtColumns = [
                        { label: "#", field: null, align: "center" as const },
                        { label: "ID Inscr.", field: "subscriptionId", align: "center" as const },
                        { label: "ID Camisa", field: "tshirtId", align: "center" as const },
                        { label: "Atleta", field: "athleteName", align: "left" as const },
                        { label: "Descr.", field: "measure", align: "left" as const },
                        { label: "Tamanho", field: "size", align: "left" as const },
                        { label: "Valor", field: "price", align: "right" as const },
                        { label: "Status", field: "status", align: "center" as const },
                        { label: "Tipo Pgto", field: "paymentType", align: "center" as const },
                      ];

                      return (
                        <>
                          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Detalhamento Individual de Camisas (<span>{filtered.length}</span>)
                            </Typography>
                            <Box display="flex" gap={0.5}>
                              {(["all", "paid", "pending", "orphan"] as const).map((f) => (
                                <Chip
                                  key={f}
                                  label={f === "all" ? `Todos (${shirtsData.details.length})` : `${statusLabel[f]} (${shirtsData.details.filter((d) => d.status === f).length})`}
                                  size="small"
                                  variant={shirtsFilter === f ? "filled" : "outlined"}
                                  color={f === "all" ? "default" : statusColor[f]}
                                  onClick={() => setShirtsFilter(f)}
                                  sx={{ cursor: "pointer" }}
                                />
                              ))}
                            </Box>
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                            <TextField
                              size="small"
                              placeholder="Buscar por atleta, tamanho, ID..."
                              value={shirtsSearch}
                              onChange={(e) => setShirtsSearch(e.target.value)}
                              sx={{
                                flex: 1,
                                minWidth: 200,
                                backgroundColor: "#fff",
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                              }}
                            />
                            <Tooltip title="Exportar para Excel">
                              <Button
                                variant="outlined"
                                onClick={exportShirtsXLSX}
                                sx={{
                                  borderColor: "#1a2252",
                                  color: "#1a2252",
                                  textTransform: "none",
                                  fontWeight: 600,
                                  borderRadius: "8px",
                                  "&:hover": { backgroundColor: "#1a2252", color: "#fff" },
                                }}
                              >
                                Exportar XLSX
                              </Button>
                            </Tooltip>
                          </Box>

                          <TableContainer
                            component={Box}
                            sx={{ maxHeight: 500, borderRadius: "12px", border: "1px solid rgba(26,34,82,0.06)", overflow: "auto" }}
                          >
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow sx={{ "& th": { backgroundColor: "#1a2252" } }}>
                                  {shirtColumns.map((col) => (
                                    <TableCell
                                      key={col.label}
                                      align={col.align}
                                      sx={styledHeaderSx}
                                    >
                                      {col.field ? (
                                        <TableSortLabel
                                          active={shirtsSortField === col.field}
                                          direction={shirtsSortField === col.field ? shirtsSortDir : "asc"}
                                          onClick={() => handleShirtsSort(col.field!)}
                                        >
                                          {col.label}
                                        </TableSortLabel>
                                      ) : (
                                        col.label
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filtered.length > 0 ? (
                                  filtered.map((d, index) => (
                                    <TableRow
                                      key={d.tshirtId}
                                      sx={{
                                        backgroundColor: index % 2 === 0 ? "#fff" : "#f7f8fb",
                                        "&:hover": { backgroundColor: "#f0f2f8" },
                                      }}
                                    >
                                      <TableCell align="center" sx={{ color: "#6b7490", fontSize: "13px", width: 40 }}>
                                        {index + 1}
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: "13px", color: "#1a2252" }} translate="no">
                                        {d.subscriptionId ?? "-"}
                                      </TableCell>
                                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: "13px", color: "#1a2252" }} translate="no">
                                        {d.tshirtId}
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: "13px", color: "#1a2252" }}>
                                        {highlightText(d.athleteName, shirtsSearch)}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>
                                        {highlightText(d.measure, shirtsSearch)}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>
                                        {highlightText(d.size, shirtsSearch)}
                                      </TableCell>
                                      <TableCell align="right" translate="no" sx={{ whiteSpace: "nowrap" }}>
                                        <Chip
                                          label={formatBRL(d.price)}
                                          size="small"
                                          sx={{ fontWeight: 700, fontSize: "12px", backgroundColor: "#dcfce7", color: "#166534" }}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <Chip
                                          label={statusLabel[d.status]}
                                          color={statusColor[d.status]}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        {d.paymentType === "auto" ? (
                                          <Chip label="Automatico" color="info" size="small" />
                                        ) : d.paymentType === "manual" ? (
                                          <Chip label="Manual" color="default" size="small" />
                                        ) : (
                                          "-"
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: "#9098b0" }}>
                                      {shirtsSearch ? "Nenhum resultado para a busca" : "Nenhum dado encontrado"}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      );
                    })()}
                  </>
                )}
          </>
        )}

        {/* ========== TAB 1: SOLICITAÇÕES DE SAQUE (ADMIN) ========== */}
        {isAdmin && adminTab === 1 && (
          <>
            {/* Cards resumo de saques */}
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr 1fr" }}
              gap={2}
              sx={{ mb: 3 }}
            >
              <SummaryCard>
                <Box display="flex" flexDirection="column">
                  <SummaryLabel>Total Pendente</SummaryLabel>
                  <SummaryValue translate="no" style={{ color: "#ed6c02" }}>
                    {formatBRL(adminTotalByStatus("PENDING"))}
                  </SummaryValue>
                  <Typography variant="caption" color="textSecondary">
                    <span>{adminWithdrawalsOnly.filter((w) => w.status === "PENDING").length}</span> solicitação(ões)
                  </Typography>
                </Box>
              </SummaryCard>
              <SummaryCard>
                <Box display="flex" flexDirection="column">
                  <SummaryLabel>Total Aprovado</SummaryLabel>
                  <SummaryValue translate="no" style={{ color: "#0288d1" }}>
                    {formatBRL(adminTotalByStatus("APPROVED"))}
                  </SummaryValue>
                  <Typography variant="caption" color="textSecondary">
                    <span>{adminWithdrawalsOnly.filter((w) => w.status === "APPROVED").length}</span> aguardando pagamento
                  </Typography>
                </Box>
              </SummaryCard>
              <SummaryCard>
                <Box display="flex" flexDirection="column">
                  <SummaryLabel>Total Pago</SummaryLabel>
                  <SummaryValue translate="no" style={{ color: "#218802" }}>
                    {formatBRL(adminTotalByStatus("PAID"))}
                  </SummaryValue>
                  <Typography variant="caption" color="textSecondary">
                    <span>{adminWithdrawalsOnly.filter((w) => w.status === "PAID").length}</span> saque(s) realizados
                  </Typography>
                </Box>
              </SummaryCard>
              <SummaryCard>
                <Box display="flex" flexDirection="column">
                  <SummaryLabel>Total Rejeitado</SummaryLabel>
                  <SummaryValue translate="no" style={{ color: "#ac0000" }}>
                    {formatBRL(adminTotalByStatus("REJECTED"))}
                  </SummaryValue>
                  <Typography variant="caption" color="textSecondary">
                    <span>{adminWithdrawalsOnly.filter((w) => w.status === "REJECTED").length}</span> rejeitado(s)
                  </Typography>
                </Box>
              </SummaryCard>
              {adminServiceChargesTotal > 0 && (
              <SummaryCard>
                <Box display="flex" flexDirection="column">
                  <SummaryLabel>Total Serviços</SummaryLabel>
                  <SummaryValue translate="no" style={{ color: "#9c27b0" }}>
                    {formatBRL(adminServiceChargesTotal)}
                  </SummaryValue>
                  <Typography variant="caption" color="textSecondary">
                    <span>{allWithdrawals.filter((w) => w.type === "SERVICE_CHARGE").length}</span> serviço(s) registrado(s)
                  </Typography>
                </Box>
              </SummaryCard>
              )}
            </Box>

            {/* Filtros */}
            <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {[
                { value: "", label: "Todos" },
                { value: "PENDING", label: "Pendentes" },
                { value: "APPROVED", label: "Aprovados" },
                { value: "PAID", label: "Pagos" },
                { value: "REJECTED", label: "Rejeitados" },
              ].map((f) => (
                <Chip
                  key={f.value}
                  label={f.label}
                  variant={adminStatusFilter === f.value ? "filled" : "outlined"}
                  color={adminStatusFilter === f.value ? "primary" : "default"}
                  onClick={() => setAdminStatusFilter(f.value)}
                  clickable
                />
              ))}
            </Box>

            {adminWithdrawalsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (() => {
              const handleAdminWdSort = (field: string) => {
                if (adminWdSortField === field) {
                  setAdminWdSortDir(adminWdSortDir === "asc" ? "desc" : "asc");
                } else {
                  setAdminWdSortField(field);
                  setAdminWdSortDir("asc");
                }
              };

              let filteredAdminWd = allWithdrawals.filter((w) => {
                if (!adminWdSearch) return true;
                const term = adminWdSearch.toUpperCase();
                return (
                  String(w.id).includes(term) ||
                  w.tournament.name.toUpperCase().includes(term) ||
                  (w.requester?.name || "").toUpperCase().includes(term) ||
                  (w.pixKey || "").toUpperCase().includes(term) ||
                  (w.description || "").toUpperCase().includes(term) ||
                  (statusConfig[w.status]?.label || w.status).toUpperCase().includes(term)
                );
              });

              if (adminWdSortField) {
                filteredAdminWd = [...filteredAdminWd].sort((a, b) => {
                  const dir = adminWdSortDir === "asc" ? 1 : -1;
                  switch (adminWdSortField) {
                    case "id": return (a.id - b.id) * dir;
                    case "type": return a.type.localeCompare(b.type) * dir;
                    case "tournament": return a.tournament.name.localeCompare(b.tournament.name, "pt-BR") * dir;
                    case "requester": return (a.requester?.name || "").localeCompare(b.requester?.name || "", "pt-BR") * dir;
                    case "amount": return (Number(a.amount) - Number(b.amount)) * dir;
                    case "status": return a.status.localeCompare(b.status) * dir;
                    case "requestedAt": return (new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime()) * dir;
                    case "paidAt": return ((a.paidAt || "").localeCompare(b.paidAt || "")) * dir;
                    default: return 0;
                  }
                });
              }

              const exportAdminWdXLSX = () => {
                const wsData = filteredAdminWd.map((w, i) => ({
                  "#": i + 1,
                  ID: w.id,
                  Tipo: w.type === "SERVICE_CHARGE" ? "Serviço" : "Saque",
                  Torneio: w.tournament.name,
                  Organizador: w.requester?.name || "-",
                  Valor: Number(w.amount),
                  PIX: w.pixKey || "-",
                  "Descrição": w.description || "-",
                  Status: statusConfig[w.status]?.label || w.status,
                  "Solicitado em": formatDate(w.requestedAt),
                  "Pago em": formatDate(w.paidAt),
                }));
                const ws = XLSX.utils.json_to_sheet(wsData);
                ws["!cols"] = [{ wch: 5 }, { wch: 8 }, { wch: 10 }, { wch: 30 }, { wch: 25 }, { wch: 12 }, { wch: 25 }, { wch: 30 }, { wch: 12 }, { wch: 18 }, { wch: 18 }];
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Solicitações de Saque");
                XLSX.writeFile(wb, "solicitacoes-saque.xlsx");
              };

              const adminWdColumns = [
                { label: "#", field: null, align: "center" as const },
                { label: "ID", field: "id", align: "center" as const },
                { label: "Tipo", field: "type", align: "center" as const },
                { label: "Torneio", field: "tournament", align: "left" as const },
                { label: "Organizador", field: "requester", align: "left" as const },
                { label: "Valor", field: "amount", align: "right" as const },
                { label: "PIX", field: null, align: "left" as const },
                { label: "Descrição", field: null, align: "left" as const },
                { label: "Status", field: "status", align: "center" as const },
                { label: "Solicitado em", field: "requestedAt", align: "left" as const },
                { label: "Pago em", field: "paidAt", align: "left" as const },
                { label: "Ações", field: null, align: "center" as const },
              ];

              return (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Buscar por ID, torneio, organizador, PIX, descrição..."
                      value={adminWdSearch}
                      onChange={(e) => setAdminWdSearch(e.target.value)}
                      sx={{ flex: 1, minWidth: 200, backgroundColor: "#fff", borderRadius: "8px", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    />
                    <Tooltip title="Exportar para Excel">
                      <Button
                        variant="outlined"
                        onClick={exportAdminWdXLSX}
                        sx={{ borderColor: "#1a2252", color: "#1a2252", textTransform: "none", fontWeight: 600, borderRadius: "8px", "&:hover": { backgroundColor: "#1a2252", color: "#fff" } }}
                      >
                        Exportar XLSX
                      </Button>
                    </Tooltip>
                  </Box>
                  <TableContainer sx={{ borderRadius: "12px", border: "1px solid rgba(26,34,82,0.06)", overflow: "auto", maxHeight: 600 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ "& th": { backgroundColor: "#1a2252" } }}>
                          {adminWdColumns.map((col) => (
                            <TableCell key={col.label} align={col.align} sx={styledHeaderSx}>
                              {col.field ? (
                                <TableSortLabel active={adminWdSortField === col.field} direction={adminWdSortField === col.field ? adminWdSortDir : "asc"} onClick={() => handleAdminWdSort(col.field!)}>
                                  {col.label}
                                </TableSortLabel>
                              ) : col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAdminWd.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={12} align="center" sx={{ py: 4, color: "#9098b0" }}>
                              {adminWdSearch ? "Nenhum resultado para a busca" : "Nenhuma solicitação encontrada"}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredAdminWd.map((w, index) => (
                            <TableRow key={w.id} sx={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f7f8fb", "&:hover": { backgroundColor: "#f0f2f8" } }}>
                              <TableCell align="center" sx={{ color: "#6b7490", fontSize: "13px", width: 40 }}>{index + 1}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, fontSize: "13px", color: "#1a2252" }}>{w.id}</TableCell>
                              <TableCell align="center">
                                <Chip label={w.type === "SERVICE_CHARGE" ? "Serviço" : "Saque"} size="small" color={w.type === "SERVICE_CHARGE" ? "secondary" : "default"} variant="outlined" />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: "13px", color: "#1a2252" }}>{highlightText(w.tournament.name, adminWdSearch)}</TableCell>
                              <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>{highlightText(w.requester?.name || "-", adminWdSearch)}</TableCell>
                              <TableCell align="right" translate="no" sx={{ whiteSpace: "nowrap" }}>
                                <Chip label={formatBRL(Number(w.amount))} size="small" sx={{ fontWeight: 700, fontSize: "12px", backgroundColor: "#dcfce7", color: "#166534" }} />
                              </TableCell>
                              <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>{highlightText(w.pixKey || "-", adminWdSearch)}</TableCell>
                              <TableCell sx={{ fontSize: "13px", color: "#353d58" }}>{highlightText(w.description || "-", adminWdSearch)}</TableCell>
                              <TableCell align="center">
                                <Tooltip title={w.rejectionReason || ""} arrow disableHoverListener={!w.rejectionReason}>
                                  <Chip label={statusConfig[w.status]?.label || w.status} color={statusConfig[w.status]?.color || "default"} size="small" />
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ fontSize: "13px", color: "#353d58", whiteSpace: "nowrap" }}>{formatDate(w.requestedAt)}</TableCell>
                              <TableCell sx={{ fontSize: "13px", color: "#353d58", whiteSpace: "nowrap" }}>{formatDate(w.paidAt)}</TableCell>
                              <TableCell align="center">
                                {w.type !== "SERVICE_CHARGE" && w.status === "PENDING" && (
                                  <Box display="flex" gap={1}>
                                    <Button size="small" variant="contained" color="success" onClick={() => setApproveDialog(w)}>Aprovar</Button>
                                    <Button size="small" variant="outlined" color="error" onClick={() => setRejectDialog(w)}>Rejeitar</Button>
                                  </Box>
                                )}
                                {w.type !== "SERVICE_CHARGE" && w.status === "APPROVED" && (
                                  <Button size="small" variant="outlined" color="primary" onClick={() => setPaidDialog(w)}>Confirmar Pagamento</Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              );
            })()}
          </>
        )}
      </Container>

      {/* ========== DIALOGS ========== */}

      {/* Dialog solicitar saque (dono) */}
      <Dialog open={withdrawDialogOpen} onClose={() => { setWithdrawDialogOpen(false); setSelectedTournamentForWithdraw(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Solicitar Saque</DialogTitle>
        <DialogContent>
          {(() => {
            const t = selectedTournamentForWithdraw || selectedTournaments[0];
            const balance = t ? balanceMap[t.id] : null;
            return (
              <>
                {selectedTournaments.length > 1 && t && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Torneio: <strong>{t.name}</strong>
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Saldo disponível: <strong translate="no">{formatBRL(balance?.availableBalance || 0)}</strong>
                </Typography>
              </>
            );
          })()}
          <TextField
            autoFocus
            fullWidth
            label="Chave PIX"
            placeholder="CPF, e-mail, telefone ou chave aleatória"
            value={withdrawPixKey}
            onChange={(e) => setWithdrawPixKey(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Valor do saque (R$)"
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            inputProps={{ min: 0.01, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleRequestWithdraw}
            disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
          >
            {withdrawLoading ? <CircularProgress size={20} /> : "Confirmar Solicitação"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog aprovar (admin) */}
      <Dialog open={!!approveDialog} onClose={() => setApproveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Aprovar Saque</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja aprovar o saque de <strong translate="no">{formatBRL(Number(approveDialog?.amount || 0))}</strong> para{" "}
            <strong>{approveDialog?.requester?.name}</strong> ({approveDialog?.tournament.name})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : "Aprovar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog rejeitar (admin) */}
      <Dialog open={!!rejectDialog} onClose={() => { setRejectDialog(null); setRejectReason(""); }} maxWidth="xs" fullWidth>
        <DialogTitle>Rejeitar Saque</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Rejeitar saque de <strong translate="no">{formatBRL(Number(rejectDialog?.amount || 0))}</strong> de{" "}
            <strong>{rejectDialog?.requester?.name}</strong>?
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Motivo da rejeição"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectDialog(null); setRejectReason(""); }}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading || !rejectReason}>
            {actionLoading ? <CircularProgress size={20} /> : "Rejeitar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar pagamento (admin) */}
      <Dialog open={!!paidDialog} onClose={() => setPaidDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar Pagamento</DialogTitle>
        <DialogContent>
          <Typography>
            Confirmar que o pagamento de <strong translate="no">{formatBRL(Number(paidDialog?.amount || 0))}</strong> foi realizado para{" "}
            <strong>{paidDialog?.requester?.name}</strong> ({paidDialog?.tournament.name})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaidDialog(null)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmPaid} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : "Confirmar Pagamento"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog registrar serviço (admin) */}
      <Dialog
        open={serviceChargeDialogOpen}
        onClose={() => { setServiceChargeDialogOpen(false); setServiceChargeTournament(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Registrar Serviço</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Torneio: <strong>{serviceChargeTournament?.name}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Este valor será debitado do saldo disponível do organizador sem necessidade de aprovação.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Descrição do serviço"
            placeholder="Ex: Acompanhamento no evento, suporte técnico..."
            value={serviceChargeDescription}
            onChange={(e) => setServiceChargeDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Valor (R$)"
            type="number"
            value={serviceChargeAmount}
            onChange={(e) => setServiceChargeAmount(e.target.value)}
            inputProps={{ min: 0.01, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setServiceChargeDialogOpen(false); setServiceChargeTournament(null); }}>Cancelar</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreateServiceCharge}
            disabled={serviceChargeLoading || !serviceChargeAmount || !serviceChargeDescription || parseFloat(serviceChargeAmount) <= 0}
          >
            {serviceChargeLoading ? <CircularProgress size={20} /> : "Registrar Serviço"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentManagement;
